/**
 * Wish Import — SCH Summer Shuttles 2026
 *
 * Admin CSV import page for booked students and interested families.
 * Stores parsed data in Firestore under campRosters/summer-2026.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { db } from '@/lib/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Upload, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import type { ParsedRider } from './WishIntelligence';

// ─── CSV Parsing ──────────────────────────────────────────────────────────────

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) return [];

  const headers = parseCSVRow(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVRow(lines[i]);
    if (values.length === 0) continue;
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? '';
    });
    rows.push(row);
  }

  return rows;
}

function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseWeeks(raw: string): number[] {
  if (!raw || raw.trim() === '') return [];
  return raw
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n));
}

function parseRate(raw: string): 165 | 75 {
  const n = parseInt(raw.replace(/[^0-9]/g, ''), 10);
  return n === 75 ? 75 : 165;
}

function isSkippableTime(val: string): boolean {
  return val.trim().toLowerCase().includes('please let us know');
}

// ─── Parse Booked Students CSV ────────────────────────────────────────────────
// Headers: "Enrollment ID","Route Offer ID","Route Name","Pickup Location","Dropoff Location",
//   "Pickup Time","Dropoff Time","Guardian Name","Guardian Email","Guardian Phone",
//   "Students","Enrolled Weeks","Weekly Rate Per Student","Subtotal","Referral Discount",
//   "Total Amount","Payment Status","Paid At","Enrollment Status","Created At"

function parseBookedCSV(rows: Record<string, string>[]): ParsedRider[] {
  return rows.map(row => {
    const rate = parseRate(row['Weekly Rate Per Student'] ?? '');
    return {
      name: row['Guardian Name'] ?? '',
      email: row['Guardian Email'] ?? '',
      phone: row['Guardian Phone'] ?? undefined,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: row['Pickup Time'] ?? undefined,
      dropoffTime: row['Dropoff Time'] ?? undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Enrolled Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'booked' as const,
      routeName: row['Route Name'] ?? undefined,
    };
  }).filter(r => r.name || r.email);
}

// ─── Parse Interested Families CSV ───────────────────────────────────────────
// Headers: "Offer ID","Route Name","Offer Status","Pickup Location","Dropoff Location",
//   "Pickup Time","Dropoff Time","Weekly Rate","Sent At","Expires At",
//   "Recipient Email","Recipient Name","Recipient Status","Students","Selected Weeks",
//   "Subtotal","Total Amount","Paid At","Enrollment ID","Quote ID"

function parseInterestedCSV(rows: Record<string, string>[]): ParsedRider[] {
  const seen = new Set<string>();
  const results: ParsedRider[] = [];

  for (const row of rows) {
    const email = row['Recipient Email'] ?? '';
    if (!email || seen.has(email)) continue;
    seen.add(email);

    const rate = parseRate(row['Weekly Rate'] ?? '');
    const pickupTimeRaw = row['Pickup Time'] ?? '';
    const dropoffTimeRaw = row['Dropoff Time'] ?? '';

    results.push({
      name: row['Recipient Name'] ?? '',
      email,
      pickup: row['Pickup Location'] ?? '',
      pickupTime: isSkippableTime(pickupTimeRaw) ? undefined : pickupTimeRaw || undefined,
      dropoffTime: isSkippableTime(dropoffTimeRaw) ? undefined : dropoffTimeRaw || undefined,
      students: parseInt(row['Students'] ?? '1', 10) || 1,
      weeks: parseWeeks(row['Selected Weeks'] ?? ''),
      rate,
      loop: rate === 75 ? 'late' : 'early',
      status: 'interested' as const,
      routeName: row['Route Name'] ?? undefined,
    });
  }

  return results;
}

// ─── Preview Table ────────────────────────────────────────────────────────────

function PreviewTable({ riders, label }: { riders: ParsedRider[]; label: string }) {
  if (riders.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {label} — {riders.length} records
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Name</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Pickup</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Students</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Weeks</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Rate</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Loop</th>
            </tr>
          </thead>
          <tbody>
            {riders.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">{r.name || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-gray-600">{r.email}</td>
                <td className="px-3 py-2 text-gray-600">{r.pickup || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-center text-gray-900">{r.students}</td>
                <td className="px-3 py-2 text-center text-gray-600">
                  {r.weeks.length > 0 ? r.weeks.join(', ') : <span className="text-gray-400">TBD</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.rate === 75 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    ${r.rate}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.loop === 'late' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {r.loop}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Upload Zone ──────────────────────────────────────────────────────────────

function UploadZone({
  label,
  description,
  fileName,
  rowCount,
  error,
  onFile,
  onClear,
  color,
}: {
  label: string;
  description: string;
  fileName: string | null;
  rowCount: number;
  error: string | null;
  onFile: (file: File) => void;
  onClear: () => void;
  color: 'blue' | 'green';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const colorMap = {
    blue: {
      border: 'border-blue-300 hover:border-blue-400',
      bg: 'bg-blue-50 hover:bg-blue-100',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-800',
    },
    green: {
      border: 'border-green-300 hover:border-green-400',
      bg: 'bg-green-50 hover:bg-green-100',
      text: 'text-green-700',
      badge: 'bg-green-100 text-green-800',
    },
  };
  const c = colorMap[color];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <div className="flex-1">
      <div className="text-sm font-semibold text-gray-700 mb-2">{label}</div>
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          fileName ? 'border-gray-300 bg-white' : `${c.border} ${c.bg}`
        }`}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => !fileName && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
        {fileName ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{fileName}</div>
                <div className="text-xs text-gray-500">{rowCount} records parsed</div>
              </div>
            </div>
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        ) : (
          <>
            <Upload className={`w-8 h-8 mx-auto mb-2 ${c.text}`} />
            <div className={`text-sm font-medium ${c.text}`}>Drop CSV here or click to browse</div>
            <div className="text-xs text-gray-500 mt-1">{description}</div>
          </>
        )}
      </div>
      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function WishImport() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [bookedRiders, setBookedRiders] = useState<ParsedRider[]>([]);
  const [interestedRiders, setInterestedRiders] = useState<ParsedRider[]>([]);
  const [bookedFileName, setBookedFileName] = useState<string | null>(null);
  const [interestedFileName, setInterestedFileName] = useState<string | null>(null);
  const [bookedError, setBookedError] = useState<string | null>(null);
  const [interestedError, setInterestedError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  const handleBookedFile = (file: File) => {
    setBookedError(null);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        const riders = parseBookedCSV(rows);
        if (riders.length === 0) {
          setBookedError('No valid records found. Check that the CSV has the expected headers.');
          return;
        }
        setBookedRiders(riders);
        setBookedFileName(file.name);
      } catch (err) {
        console.error('Error parsing booked CSV:', err);
        setBookedError('Failed to parse CSV. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const handleInterestedFile = (file: File) => {
    setInterestedError(null);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        const riders = parseInterestedCSV(rows);
        if (riders.length === 0) {
          setInterestedError('No valid records found. Check that the CSV has the expected headers.');
          return;
        }
        setInterestedRiders(riders);
        setInterestedFileName(file.name);
      } catch (err) {
        console.error('Error parsing interested CSV:', err);
        setInterestedError('Failed to parse CSV. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const clearBooked = () => {
    setBookedRiders([]);
    setBookedFileName(null);
    setBookedError(null);
  };

  const clearInterested = () => {
    setInterestedRiders([]);
    setInterestedFileName(null);
    setInterestedError(null);
  };

  const handleSave = async () => {
    if (!user) return;
    if (bookedRiders.length === 0 && interestedRiders.length === 0) {
      toast({
        title: 'No data to save',
        description: 'Upload at least one CSV before saving.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      await setDoc(doc(db, 'campRosters', 'summer-2026'), {
        season: 'Summer 2026',
        bookedRiders,
        interestedRiders,
        updatedAt: serverTimestamp(),
        importedBy: user.uid,
      });
      toast({
        title: 'Saved!',
        description: `${bookedRiders.length} booked and ${interestedRiders.length} interested riders saved to Firestore.`,
      });
      navigate('/admin/wish-intelligence');
    } catch (err) {
      console.error('Error saving to Firestore:', err);
      toast({
        title: 'Save failed',
        description: 'Could not save to Firestore. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const hasAnyData = bookedRiders.length > 0 || interestedRiders.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-shuttlebee-yellow rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-gray-900" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Wish Import</h1>
          </div>
          <p className="text-gray-600 ml-11">
            SCH Summer Shuttles 2026 · Upload CSV exports to update the roster
          </p>
        </div>

        {/* Upload Zones */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Upload CSVs</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <UploadZone
                label="Booked Students"
                description="Export from Enrollments — includes Guardian Name, Enrolled Weeks, Weekly Rate Per Student"
                fileName={bookedFileName}
                rowCount={bookedRiders.length}
                error={bookedError}
                onFile={handleBookedFile}
                onClear={clearBooked}
                color="blue"
              />
              <UploadZone
                label="Interested Families"
                description="Export from Route Offers — includes Recipient Name, Selected Weeks, Weekly Rate"
                fileName={interestedFileName}
                rowCount={interestedRiders.length}
                error={interestedError}
                onFile={handleInterestedFile}
                onClear={clearInterested}
                color="green"
              />
            </div>
          </CardContent>
        </Card>

        {/* Previews */}
        {hasAnyData && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Preview</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <PreviewTable riders={bookedRiders} label="Booked Students" />
              <PreviewTable riders={interestedRiders} label="Interested Families" />
            </CardContent>
          </Card>
        )}

        {/* Save */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {hasAnyData
              ? `Ready to save: ${bookedRiders.length} booked · ${interestedRiders.length} interested`
              : 'Upload at least one CSV to continue'}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/wish-intelligence')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasAnyData || saving}
              className="flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save to Firestore
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Schema hints */}
        <Card className="mt-8 border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Expected CSV Headers</CardTitle>
          </CardHeader>
          <CardContent className="p-4 text-xs text-gray-500 space-y-3">
            <div>
              <div className="font-semibold text-gray-700 mb-1">Booked Students</div>
              <div className="font-mono bg-gray-50 rounded p-2 leading-relaxed">
                "Enrollment ID","Route Offer ID","Route Name","Pickup Location","Dropoff Location",
                "Pickup Time","Dropoff Time","Guardian Name","Guardian Email","Guardian Phone",
                "Students","Enrolled Weeks","Weekly Rate Per Student","Subtotal","Referral Discount",
                "Total Amount","Payment Status","Paid At","Enrollment Status","Created At"
              </div>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-1">Interested Families</div>
              <div className="font-mono bg-gray-50 rounded p-2 leading-relaxed">
                "Offer ID","Route Name","Offer Status","Pickup Location","Dropoff Location",
                "Pickup Time","Dropoff Time","Weekly Rate","Sent At","Expires At",
                "Recipient Email","Recipient Name","Recipient Status","Students","Selected Weeks",
                "Subtotal","Total Amount","Paid At","Enrollment ID","Quote ID"
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
